import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

/** Below this the elapsed time is noise; past it, a ticking count shows the work is alive. */
export const SHOW_ELAPSED_AFTER_S = 3;

interface AiWorkingIndicatorProps {
  /** What is happening, as a sentence fragment ("Generating title and description…"). */
  label: string;
  /** When the work started (ISO or Date); when set, the elapsed time is shown once it passes 3 s. */
  since?: string | Date | null;
  /** Secondary line, e.g. the reason the last attempt failed. */
  detail?: string | null;
  className?: string;
  'data-testid'?: string;
}

/**
 * The one "AI is working on this" line: a spinner, what is happening and for how long.
 *
 * AI work runs in a queue worker for seconds to minutes after the button's request returned,
 * so the indicator follows the server-reported state, not the request. It is a polite live
 * region (WCAG 4.1.3): a screen reader hears the label change without focus moving.
 */
export function AiWorkingIndicator({
  label,
  since,
  detail,
  className,
  'data-testid': testId,
}: AiWorkingIndicatorProps): JSX.Element {
  const elapsed = useElapsedSeconds(since ?? null);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex items-start gap-2 text-sm text-muted-foreground', className)}
      data-testid={testId}
    >
      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 motion-safe:animate-spin" aria-hidden="true" />
      <div>
        <p>
          {label}
          {elapsed !== null && elapsed >= SHOW_ELAPSED_AFTER_S && (
            // Not announced every second: the count is visual reassurance, not news.
            <span aria-hidden="true" className="tabular-nums" data-testid={testId ? `${testId}-elapsed` : undefined}>
              {' '}· {formatElapsed(elapsed)}
            </span>
          )}
        </p>
        {detail && <p className="mt-0.5 text-xs">{detail}</p>}
      </div>
    </div>
  );
}

/** Seconds since `since`, ticking once a second; null without a start time. */
function useElapsedSeconds(since: string | Date | null): number | null {
  const start = since === null ? null : new Date(since).getTime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (start === null) return undefined;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [start]);

  if (start === null || Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((now - start) / 1000));
}

/** 42 → "42s", 135 → "2m 15s". */
export function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}m ${String(seconds % 60)}s`;
}
